import { HStack, Image, Skeleton, Text, VStack } from '@chakra-ui/react';
import React from 'react';
import { useQueryClient } from 'react-query';
import { Navigate, useSearchParams } from 'react-router-dom';

import { AppError } from '../../../../framework/models/errors';
import { TypeUtils } from '../../../../framework/utils/UtilityTypes';
import { useCheckArea } from '../../../components/Check';
import { BackButton, DecideButton } from '../../../components/StyledButton';
import { pageNames, pagePaths } from '../../../constants/Page';
import { useGlobalStateMachine } from '../../../GlobalStore';
import {
  DateChangerState,
  generateInitialState,
  useDateChangers,
} from '../../../hooks/DateChangers';
import { useEngeneNavigate } from '../../../hooks/navigate';
import {
  DownloadAttachedResult,
  useGetAttachedFile,
} from '../../../hooks/queries/attachedFile';
import { usePostDeadlineSearch } from '../../../hooks/queries/deadline';
import {
  SALARY_CONFIRM_CONTEXT_NAMES,
  usePostSalaryConfirmSearch,
} from '../../../hooks/queries/salaryConfirm';
import { usePostWorkApplyPublishConfirm } from '../../../hooks/queries/workApply';
import useToastHook from '../../../hooks/ToastHook';
import { DeadlineMode } from '../../../models/gen/deadlineMode';
import { FileKind } from '../../../models/gen/fileKind';
import { SalaryConfirmSearchResult } from '../../../models/gen/salaryConfirmSearchResult';
import { useSizeContext } from '../../SizeProvider';
import Wireframe from '../../wireframe/Wireframe';

/**
 * 給与確認画面
 * @returns
 */
const Payslip: React.FunctionComponent = () => {
  const { actions, state } = useGlobalStateMachine();

  if (TypeUtils.isNil(state.userInfo)) {
    return <Navigate to={pagePaths.ROOT} />;
  }

  const queryClient = useQueryClient();
  const data = queryClient.getQueryData<SalaryConfirmSearchResult>(
    SALARY_CONFIRM_CONTEXT_NAMES.POST_SALARY_CONFIRM_SEARCH
  );
  const setData = React.useCallback(
    (result: SalaryConfirmSearchResult) =>
      queryClient.setQueryData(
        SALARY_CONFIRM_CONTEXT_NAMES.POST_SALARY_CONFIRM_SEARCH,
        result
      ),
    [queryClient]
  );
  const sizes = useSizeContext();

  const navigate = useEngeneNavigate();
  const [searchParams] = useSearchParams();
  const {
    MonthChanger,
    setState,
    state: monthState,
  } = useDateChangers({
    initialState:
      generateInitialState(searchParams, ['year', 'month']) ||
      state.pageInfo.PAYSLIP?.dateState,
  });
  const { CheckArea, checked, off } = useCheckArea();
  const [file, setFile] = React.useState<DownloadAttachedResult>();

  const postSalaryConfirmSearch = usePostSalaryConfirmSearch();
  const { data: deadlines, refetch: refetchDeadlines } = usePostDeadlineSearch({
    modes: [DeadlineMode.Payslip],
  });
  const getAttachedFile = useGetAttachedFile();
  const onChangeChanger = (dateState: DateChangerState) => {
    actions.updatePageInfo({ PAYSLIP: { dateState } });
    off();
    postSalaryConfirmSearch.mutate(
      {
        year: dateState.year,
        month: dateState.month,
      },
      {
        onSuccess: (result) => {
          if (!TypeUtils.isNil(result)) {
            setData(result);
          }
        },
        onError: (error) =>
          toast(
            error instanceof AppError
              ? error
              : '給与確認情報の取得に失敗しました'
          ),
      }
    );
  };
  React.useState(() => {
    refetchDeadlines();
    onChangeChanger(monthState);
  });
  React.useEffect(() => {
    const newState =
      generateInitialState(searchParams, ['year', 'month']) ||
      state.pageInfo.PAYSLIP?.dateState;
    onChangeChanger(newState);
    setState(newState);
  }, [searchParams]);
  React.useEffect(() => {
    if (
      !postSalaryConfirmSearch.isLoading &&
      TypeUtils.isNonNilNumber(data?.result?.salaryConfirmId) &&
      TypeUtils.isNonNilString(data?.result?.publishTime)
    ) {
      getAttachedFile.mutate(
        [
          {
            mode: FileKind.Salary,
            id: data?.result?.salaryConfirmId,
          },
        ],
        {
          onSuccess: (results) => {
            if (Array.isArray(results)) {
              setFile(results[0]);
            } else setFile(undefined);
          },
          onError: (error) => {
            toast(
              error instanceof AppError ? error : '給与明細取得に失敗しました'
            );
            setFile(undefined);
          },
        }
      );
    }
  }, [postSalaryConfirmSearch.isLoading, data?.result]);

  const { toast } = useToastHook();
  const postWorkApplyPublishConfirm = usePostWorkApplyPublishConfirm();

  return (
    <Wireframe title={pageNames.PAYSLIP} topMessages={deadlines?.results}>
      <VStack width={sizes.width.styledMessageList}>
        <MonthChanger
          onChangeLeft={onChangeChanger}
          onChangeRight={onChangeChanger}
        />
        <Skeleton isLoaded={!postSalaryConfirmSearch.isLoading} width="100%">
          {TypeUtils.isNilNumber(data?.result?.salaryConfirmId) ||
          TypeUtils.isNilString(data?.result?.publishTime) ? (
            '給与明細が発行されていません'
          ) : TypeUtils.isNil(file?.file) ? (
            '給与明細が取得できません'
          ) : (
            <Image src={URL.createObjectURL(file!.file!)} />
          )}
          <CheckArea
            additionalArea={
              <VStack>
                <Text color="red" fontWeight="bold">
                  !!! 注意 !!! 一度確定すると原則訂正できません
                </Text>
                <Text color="red" fontWeight="bold">
                  必ず入力内容を確認の上、間違いのないことを確認してから確定してください
                </Text>
              </VStack>
            }
            button={DecideButton}
            buttonProps={{
              isDisabled:
                !checked ||
                TypeUtils.isNilNumber(data?.result?.salaryConfirmId) ||
                TypeUtils.isNilNumber(data?.result?.salaryConfirmVersion),
            }}
            confirmMessage="上記内容を了承します"
            doneMessage={
              !TypeUtils.isNilString(data?.result?.confirmTime)
                ? '給与確認が完了しています'
                : undefined
            }
            hidden={
              TypeUtils.isNilNumber(data?.result?.salaryConfirmId) ||
              TypeUtils.isNilString(data?.result?.publishTime) ||
              TypeUtils.isNilNumber(data?.result?.salaryConfirmVersion)
            }
            onClick={() => {
              actions.setGuard(true);
              postWorkApplyPublishConfirm.mutate(
                {
                  id: data?.result?.salaryConfirmId,
                  version: data?.result?.salaryConfirmVersion,
                },
                {
                  onSuccess: () => {
                    refetchDeadlines();
                    navigate();
                    actions.setGuard(false);
                  },
                  onError: (error) => {
                    toast(
                      error instanceof AppError
                        ? error
                        : '給与確認に失敗しました'
                    );
                    actions.setGuard(false);
                  },
                }
              );
            }}
          />
        </Skeleton>
        <HStack width="100%">
          <BackButton onClick={() => navigate(-1)} />
        </HStack>
      </VStack>
    </Wireframe>
  );
};

export default Payslip;
